/**
 * WorkflowNavigation Component
 *
 * Renders workflow navigation items in the sidebar under a collapsible
 * "Workflows" accordion section. Fetches workflows from the backend API
 * and displays them with access control indicators.
 *
 * Issue #1925 - Retire Persona Marketplace
 */

import React, { useState } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import {
  ListItem,
  ListItemText,
  Tooltip,
  Box,
  Collapse,
  Typography,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";

import { nuclearn_api_url } from "src/App";
import { useChatStore } from "src/views/assistant/stores/ChatStore";

interface WorkflowNavItem {
  id: number;
  slug: string;
  display_label: string;
  description: string | null;
  required_product: string | null;
  access_granted: boolean;
  config_required: boolean;
  config_completed: boolean;
  icon_name: string | null;
  introduction_prompt: string | null;
  unbound_slots: string[];
}

interface WorkflowNavigationProps {
  collapsed: boolean;
}

const fetchWorkflows = async (): Promise<WorkflowNavItem[]> => {
  const response = await axios.get(`${nuclearn_api_url}/chat/workflows`);
  return response.data;
};

const trackWorkflowClick = async (workflowId: number, workflowSlug: string) => {
  try {
    await axios.post(`${nuclearn_api_url}/chat/workflows/track-click`, {
      workflow_id: workflowId,
      workflow_slug: workflowSlug,
    });
  } catch (error) {
    console.warn("Failed to track workflow click:", error);
  }
};

const WorkflowNavigation: React.FC<WorkflowNavigationProps> = ({
  collapsed,
}) => {
  const history = useHistory();
  const { setForceAutoStart, setPersonaForWorkflow } = useChatStore();
  const [open, setOpen] = useState(true);

  const {
    data: allWorkflows,
    isLoading,
    error,
  } = useQuery<WorkflowNavItem[]>("workflowNavItems", fetchWorkflows, {
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Sort workflows: accessible+configured first, then accessible+unconfigured, then no-access
  const workflows = allWorkflows
    ? [...allWorkflows].sort((a, b) => {
        const score = (w: WorkflowNavItem) => {
          if (w.access_granted && (!w.config_required || w.config_completed))
            return 0;
          if (w.access_granted && w.config_required && !w.config_completed)
            return 1;
          return 2; // no access
        };
        return score(a) - score(b);
      })
    : undefined;

  const handleWorkflowClick = async (
    e: React.MouseEvent,
    workflow: WorkflowNavItem,
  ) => {
    e.preventDefault();

    if (!workflow.access_granted) return;

    // Track the click for analytics
    trackWorkflowClick(workflow.id, workflow.slug);

    // Set up the persona for workflow navigation
    setPersonaForWorkflow({
      id: workflow.id,
      slug: workflow.slug,
      name: workflow.display_label,
      introduction_prompt: workflow.introduction_prompt,
      required_product: workflow.required_product,
      access_granted: workflow.access_granted,
      config_required: workflow.config_required,
      config_completed: workflow.config_completed,
      unbound_slots: workflow.unbound_slots ?? [],
    });

    // Enable force auto-start for talk-first behavior.
    // Denied workflows also auto-start — the backend sends a denial template.
    setForceAutoStart(true);

    // Navigate to assistant with persona slug so useSyncPersonaToStore resolves it
    history.push(`/assistant?persona=${workflow.slug}`);
  };

  if (error || !workflows || workflows.length === 0) {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={20} sx={{ color: "rgba(255,255,255,0.6)" }} />
        </Box>
      );
    }
    return null;
  }

  // When sidebar is collapsed, just show a single icon
  if (collapsed) {
    return (
      <>
        {workflows.map((workflow) => (
          <Tooltip
            key={workflow.id}
            title={workflow.display_label}
            placement="right"
            arrow
          >
            <ListItem
              component="div"
              onClick={(e: React.MouseEvent) =>
                handleWorkflowClick(e, workflow)
              }
              data-attr={`sidebar:link:workflow-${workflow.slug}`}
              sx={{
                justifyContent: "center",
                py: 0.5,
                opacity: workflow.access_granted ? 1 : 0.6,
                cursor: workflow.access_granted ? "pointer" : "default",
                pointerEvents: workflow.access_granted ? "auto" : "none",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              {!workflow.access_granted ? (
                <LockOutlinedIcon
                  sx={{ fontSize: 18, color: "rgba(255,255,255,0.5)" }}
                />
              ) : workflow.config_required && !workflow.config_completed ? (
                <SettingsIcon sx={{ fontSize: 18, color: "#FFA726" }} />
              ) : (
                <PlayArrowIcon
                  sx={{ fontSize: 18, color: "rgba(255,255,255,0.8)" }}
                />
              )}
            </ListItem>
          </Tooltip>
        ))}
      </>
    );
  }

  return (
    <Box>
      {/* Accordion header */}
      <ListItem
        component="div"
        onClick={() => setOpen(!open)}
        data-attr="sidebar:btn:toggle-workflows"
        sx={{
          pl: "6px",
          pr: 1,
          py: 0.25,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.05)",
          },
        }}
      >
        <ListItemText
          primary={
            <Typography
              variant="caption"
              sx={{
                fontSize: "11.2px",
                color: "rgba(255,255,255,0.6)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Workflows
            </Typography>
          }
        />
        {open ? (
          <ExpandLessIcon
            sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }}
          />
        ) : (
          <ExpandMoreIcon
            sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }}
          />
        )}
      </ListItem>

      {/* Collapsible workflow list */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        {workflows.map((workflow) => (
          <Tooltip
            key={workflow.id}
            title={
              !workflow.access_granted
                ? workflow.required_product
                  ? `Requires ${workflow.required_product} subscription`
                  : "Requires a subscription"
                : workflow.config_required && !workflow.config_completed
                  ? "Admin Configuration Recommended"
                  : workflow.description || workflow.display_label
            }
            placement="right"
            arrow
          >
            <div className="the-sidebar-nav-link">
              <ListItem
                component="div"
                onClick={(e: React.MouseEvent) =>
                  handleWorkflowClick(e, workflow)
                }
                data-attr={`sidebar:link:workflow-${workflow.slug}`}
                sx={{
                  pl: "12px",
                  pr: 0,
                  alignItems: "center",
                  justifyContent: "flex-start",
                  opacity: workflow.access_granted ? 1 : 0.6,
                  cursor: workflow.access_granted ? "pointer" : "default",
                  pointerEvents: workflow.access_granted ? "auto" : "none",
                }}
                className="the-sidebar-nav-link"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 24,
                  }}
                >
                  {!workflow.access_granted ? (
                    <LockOutlinedIcon
                      sx={{ fontSize: 18, color: "rgba(255,255,255,0.5)" }}
                    />
                  ) : workflow.config_required && !workflow.config_completed ? (
                    <SettingsIcon sx={{ fontSize: 18, color: "#FFA726" }} />
                  ) : (
                    <PlayArrowIcon
                      sx={{ fontSize: 18, color: "rgba(255,255,255,0.8)" }}
                    />
                  )}
                </Box>
                <ListItemText
                  primary={workflow.display_label}
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: "13px",
                      fontStyle: !workflow.access_granted ? "italic" : "normal",
                    },
                    ml: 1,
                  }}
                />
              </ListItem>
            </div>
          </Tooltip>
        ))}
      </Collapse>
    </Box>
  );
};

export default WorkflowNavigation;
